/**
 * EAA-2029: strip above the AI chat — pick which questions an edit may touch, see what
 * the last edit changed, get warned when it dropped questions or switched language, and
 * undo it in one step. Also asks before a message regenerates the whole set.
 */

import React from 'react';
import type { AIGeneratorColors } from './AIContentGenerator.types';
import type { AIGeneratorTranslations } from './i18n';
import type { ChatEditAssessment, ChatEditRejection } from './chatEdit';

export interface ChatEditOutcome {
    changedItems: number[];
    rejected: ChatEditRejection[];
    assessment: ChatEditAssessment | null;
    canUndo: boolean;
    notice?: 'undone' | 'undoFailed' | 'rollbackFailed';
}

export interface ChatEditPanelProps {
    itemCount: number;
    selected: number[];
    onToggle: (index: number | null) => void;
    outcome: ChatEditOutcome | null;
    onUndo: () => void;
    undoing: boolean;
    onDismiss: () => void;
    pendingRegenerate: boolean;
    onConfirmRegenerate: () => void;
    onCancelRegenerate: () => void;
    disabled?: boolean;
    colors: AIGeneratorColors;
    t: AIGeneratorTranslations;
}

const fill = (template: string, values: Record<string, string | number>) =>
    template.replace(/\{(\w+)\}/g, (_, k: string) => String(values[k] ?? ''));

export const ChatEditPanel: React.FC<ChatEditPanelProps> = ({
    itemCount,
    selected,
    onToggle,
    outcome,
    onUndo,
    undoing,
    onDismiss,
    pendingRegenerate,
    onConfirmRegenerate,
    onCancelRegenerate,
    disabled,
    colors,
    t,
}) => {
    const langName = (lang: 'vi' | 'en' | null) =>
        lang === 'vi' ? t.languageVi : lang === 'en' ? t.languageEn : '';

    const chip = (active: boolean): React.CSSProperties => ({
        border: `1px solid ${active ? colors.primary : colors.border}`,
        background: active ? colors.surface : colors.background,
        color: active ? colors.primary : colors.textMuted,
        borderRadius: 999,
        padding: '2px 10px',
        fontSize: 12,
        cursor: disabled ? 'not-allowed' : 'pointer',
    });
    const actionButton: React.CSSProperties = {
        border: `1px solid ${colors.border}`,
        background: colors.background,
        color: colors.text,
        borderRadius: 6,
        padding: '4px 10px',
        fontSize: 12,
        cursor: 'pointer',
    };

    const warnings: string[] = [];
    if (outcome?.assessment?.droppedItems) {
        warnings.push(
            fill(t.chatEditDropped, {
                before: outcome.assessment.countBefore,
                after: outcome.assessment.countAfter,
            })
        );
    }
    if (outcome?.assessment?.languageChanged) {
        warnings.push(
            fill(t.chatEditLanguage, {
                before: langName(outcome.assessment.languageBefore),
                after: langName(outcome.assessment.languageAfter),
            })
        );
    }

    return (
        <div
            className="ai-generator-chat-edit"
            style={{ display: 'flex', flexDirection: 'column', gap: 8, padding: '8px 12px' }}
        >
            {itemCount > 1 && (
                <div
                    className="ai-generator-chat-edit-targets"
                    role="group"
                    aria-label={t.chatEditTargetsLabel}
                    style={{ display: 'flex', flexWrap: 'wrap', alignItems: 'center', gap: 6 }}
                >
                    <span style={{ fontSize: 12, color: colors.textMuted }}>
                        {t.chatEditTargetsLabel}
                    </span>
                    <button
                        type="button"
                        disabled={disabled}
                        aria-pressed={selected.length === 0}
                        style={chip(selected.length === 0)}
                        onClick={() => onToggle(null)}
                    >
                        {t.chatEditTargetAll}
                    </button>
                    {Array.from({ length: itemCount }, (_, i) => (
                        <button
                            key={i}
                            type="button"
                            disabled={disabled}
                            aria-pressed={selected.includes(i)}
                            style={chip(selected.includes(i))}
                            onClick={() => onToggle(i)}
                        >
                            {fill(t.chatEditQuestion, { n: i + 1 })}
                        </button>
                    ))}
                </div>
            )}

            {pendingRegenerate && (
                <div
                    role="alertdialog"
                    className="ai-generator-chat-edit-confirm"
                    style={{
                        border: `1px solid ${colors.info}`,
                        background: colors.surface,
                        borderRadius: 8,
                        padding: 10,
                        fontSize: 13,
                        color: colors.text,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 8,
                    }}
                >
                    <span>{t.chatEditRegenerateConfirm}</span>
                    <div style={{ display: 'flex', gap: 8 }}>
                        <button type="button" style={actionButton} onClick={onConfirmRegenerate}>
                            {t.chatEditRegenerateYes}
                        </button>
                        <button type="button" style={actionButton} onClick={onCancelRegenerate}>
                            {t.chatEditRegenerateNo}
                        </button>
                    </div>
                </div>
            )}

            {outcome && (
                <div
                    role={warnings.length > 0 ? 'alert' : 'status'}
                    className="ai-generator-chat-edit-outcome"
                    style={{
                        border: `1px solid ${warnings.length > 0 ? colors.error : colors.border}`,
                        background: colors.surface,
                        borderRadius: 8,
                        padding: 10,
                        fontSize: 13,
                        color: colors.text,
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 6,
                    }}
                >
                    {outcome.notice === 'rollbackFailed' && <span>{t.rollbackFailed}</span>}
                    {outcome.notice === 'undone' && <span>{t.chatEditUndone}</span>}
                    {outcome.notice === 'undoFailed' && <span>{t.chatEditUndoFailed}</span>}
                    {!outcome.notice && (
                        <span>
                            {outcome.changedItems.length > 0
                                ? fill(t.chatEditChanged, {
                                      items: outcome.changedItems
                                          .map((i) => fill(t.chatEditQuestion, { n: i + 1 }))
                                          .join(', '),
                                  })
                                : t.chatEditNoChange}
                        </span>
                    )}
                    {!outcome.notice && outcome.rejected.length > 0 && (
                        <span style={{ color: colors.textMuted }}>
                            {fill(t.chatEditRejected, { n: outcome.rejected.length })}
                        </span>
                    )}
                    {warnings.map((w) => (
                        <strong key={w} style={{ color: colors.error }}>
                            {w}
                        </strong>
                    ))}
                    <div style={{ display: 'flex', gap: 8 }}>
                        {outcome.canUndo && !outcome.notice && (
                            <button
                                type="button"
                                style={actionButton}
                                disabled={undoing}
                                onClick={onUndo}
                            >
                                {undoing ? t.chatEditUndoing : t.chatEditUndo}
                            </button>
                        )}
                        <button type="button" style={actionButton} onClick={onDismiss}>
                            {t.dismissButton}
                        </button>
                    </div>
                </div>
            )}
        </div>
    );
};

export default ChatEditPanel;
